import {RequestInfo} from "@repo/shared-core";

type QueryParams = Record<string, string>;

// sync-start:cookie-consent-categories 1255356163 https://github.com/Khan/webapp/blob/master/pkg/web/cookie_consent.go
export enum CookieCategory {
    // Short of using browser settings to block all cookies, users cannot
    // opt out of Stricly Necessary cookies. These cookies are needed for core
    // functionality of our product -- e.g. authentication, consuming content,
    // and tracking progress.
    StrictlyNecessary = "STRICTLY_NECESSARY",

    // We use Performance cookies to better understand how users interact with
    // Khan Academy -- e.g. to gain visibility into usage trends or to measure
    // performance.
    Performance = "PERFORMANCE",

    // Functional cookies provide user-facing benefits, and the usefulness or
    // joy provided by certain features may be diminished without these
    // cookies. However, a cookie set for "functional" purposes is not
    // _required_ in order to experience the main benefits of KA. Examples of
    // setting a cookie for "functional" purposes include keeping a modal
    // dismissed or for referencing a user's settings.
    Functional = "FUNCTIONAL",

    // Targeting cookies are used for identifying or connecting with people who
    // may be interested in supporting our mission (e.g., identifying potential
    // donors for marketing purposes). Such cookies should not be set if the
    // user may be underage.
    Targeting = "TARGETING",
}
// sync-end:cookie-consent-categories

// This cookie is set by OneTrust; see
// https://developer.onetrust.com/onetrust/docs/onetrust-cookies#optanonconsent
// sync-start:consent-cookie-name 1173234985 https://github.com/Khan/webapp/blob/master/pkg/web/cookie_consent.go
export const consentCookieName = "OptanonConsent";
// sync-end:consent-cookie-name

// Countries where the model is "opt-out" -- i.e., in the absence of
// consent data, all cookies are permitted because the user must opt-out of
// any they do not wish to allow. This name might be a bit confusing since
// WE'RE effectively opting them "in" to cookies, but the naming here aligns
// with how this model is referred to in compliance contexts at large, where
// "opt-out" or "opt-in" models refer to what action the USER may take.
//
// This set of countries is based on what our legal team has determined to
// be appropriate, and should stay aligned with what they've configured in
// the OneTrust app.
// sync-start:cookie-consent-opt-out 82750598 https://github.com/Khan/webapp/blob/master/pkg/web/cookie_consent.go
const optOutCountries: ReadonlyArray<string> = [
    // All countries should be represented by their (uppercase) 2-character
    // ISO-3166-2 code
    "IN", // India
    "PE", // Peru
    "US", // United States
];
// sync-end:cookie-consent-opt-out

// Default cookie consent for situations where the user hasn't explicitly
// consented to any cookies.
// sync-start:default-cookie-consent 283386877 https://github.com/Khan/webapp/blob/master/pkg/web/cookie_consent.go
export const defaultCookieConsent = function (): ReadonlyMap<
    CookieCategory,
    boolean
> {
    if (
        optOutCountries.some((country) => RequestInfo.remoteCountry === country)
    ) {
        return new Map([
            [CookieCategory.StrictlyNecessary, true],
            [CookieCategory.Performance, true],
            [CookieCategory.Functional, true],
            [CookieCategory.Targeting, false],
        ]);
    }

    return new Map([
        [CookieCategory.StrictlyNecessary, true],
        [CookieCategory.Performance, false],
        [CookieCategory.Functional, false],
        [CookieCategory.Targeting, false],
    ]);
};
// sync-end:default-cookie-consent

// Mapping of keys defined in the OneTrust cookie to the CookieCategory enum
// See parseConsentCookie for more details
// sync-start:consent-category-map 518362398 https://github.com/Khan/webapp/blob/master/pkg/web/cookie_consent.go
export const oneTrustCookieKeyToCategory: Map<string, CookieCategory> = new Map(
    [
        ["C0001", CookieCategory.StrictlyNecessary],
        ["C0002", CookieCategory.Performance],
        ["C0003", CookieCategory.Functional],
        ["C0004", CookieCategory.Targeting],
    ],
);
// sync-end:consent-category-map

// Creates a cookie, returning true if the cookie was successfully created and
// false if not.
export const createCookie = function (
    name: string,
    cookieCategory: CookieCategory,
    value?: string | number | boolean | null,
    days?: number,
    domain?: string,
    isSecure?: boolean,
    // By default, the SameSite attribute is set to Lax because we only
    // want to support our cookies in a first-party context, as well as
    // when third parties redirect to our site.
    sameSite: "None" | "Lax" | "Strict" = "Lax",
): boolean {
    if (
        // Strictly necessary cookies can always be created
        cookieCategory &&
        cookieCategory !== CookieCategory.StrictlyNecessary &&
        // We also allow cookies in any category to be created in dev
        !RequestInfo.IS_DEV_SERVER
    ) {
        const consentCookie = readCookie(consentCookieName);
        const acceptedCookieGroups = !consentCookie
            ? defaultCookieConsent()
            : parseConsentCookie(consentCookie);
        // if the user has not accepted the cookie category, do not set the cookie
        if (!acceptedCookieGroups.get(cookieCategory)) {
            return false;
        }
    }
    let expires: string;
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
    } else {
        expires = "";
    }
    const domainPart = domain ? `; domain=${domain}` : "";
    const secure = isSecure ? "; secure" : "";
    document.cookie =
        name +
        "=" +
        String(value) +
        expires +
        domainPart +
        secure +
        "; SameSite=" +
        sameSite +
        "; path=/";
    return true;
};

export const readCookie = function (name: string): string | null | undefined {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") {
            c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) === 0) {
            return c.substring(nameEQ.length, c.length);
        }
    }
    return null;
};

export const eraseCookie = function (name: string, domain?: string): undefined {
    createCookie(name, CookieCategory.StrictlyNecessary, "", -1, domain);
};

export const areCookiesEnabled = function (): boolean {
    createCookie(
        "detectCookiesEnabled",
        CookieCategory.StrictlyNecessary,
        "KhanAcademy",
    );
    if (readCookie("detectCookiesEnabled") == null) {
        return false;
    }
    eraseCookie("detectCookiesEnabled");
    return true;
};

/**
 * Set cookies in the browser based on certain URL query parameters.  Supports
 * feature flags and bigbingo experiments.
 *
 * @param {{[key: string]: string}} queryParams key-value pairs parsed from the URL.
 */
export function createCookiesFromURL(queryParams: QueryParams) {
    const flagSettings = cookieStringForFlags(queryParams);
    if (flagSettings) {
        createCookie("flags", CookieCategory.Functional, flagSettings, 365);
    }
}

function cookieStringForFlags(queryParams: QueryParams) {
    const flagsOn = queryParams["flags_on"];
    const flagsOff = queryParams["flags_off"];
    const flagsOnString = flagsOn ? flagsOn.replace(/,/g, "\n") : "";
    const flagsOffString = flagsOff ? flagsOff.replace(/,/g, "\n") : "";

    if (!flagsOnString && !flagsOffString) {
        return null;
    }
    return `${encodeURIComponent(`${flagsOnString}|${flagsOffString}`)}`;
}

// sync-start:parse-consent-cookie 685143061 https://github.com/Khan/webapp/blob/master/pkg/web/cookie_consent.go
export function parseConsentCookie(
    cookie: string,
): ReadonlyMap<CookieCategory, boolean> {
    // This logic is specific to parsing a cookie set by OneTrust:
    // https://developer.onetrust.com/onetrust/docs/onetrust-cookies#optanonconsent

    // The cookie value contains several ampersand-delimited "fields," where
    // we're interested in the "groups" field, which contains the information
    // about which cookie categories the user has consented for us to use.

    // This will have the form of 'C0001:1,C0003:1,C0002:1,C0004:1', where the
    // keys map to the cookie categories defined in the CookieCategory enum
    const rawCookieConsentGroups = new URLSearchParams(cookie).get("groups");

    // If the cookie is malformed, we'll just return the default cookie consent
    if (rawCookieConsentGroups == null) {
        return defaultCookieConsent();
    }

    // Now we need to parse the string form of the cookie consent groups into
    // a Map<CookieCategory, boolean>
    const parsedCookieConsentGroups: Map<CookieCategory, boolean> = new Map();
    for (const pair of rawCookieConsentGroups.split(",")) {
        // pair will have the form of 'C0001:1'
        const [rawCategory, value] = pair.split(":");
        // get the enum cookie category from the string category
        const category = oneTrustCookieKeyToCategory.get(rawCategory);
        if (category) {
            parsedCookieConsentGroups.set(category, value === "1");
        } else {
            // something is malformed, return default cookie consent
            return defaultCookieConsent();
        }
    }

    // If the cookie is missing any categories, we'll return the default cookie
    // consent
    return parsedCookieConsentGroups.size === oneTrustCookieKeyToCategory.size
        ? parsedCookieConsentGroups
        : defaultCookieConsent();
}
// sync-end:parse-consent-cookie

export const getConsentCookieCategories = (): ReadonlyMap<
    CookieCategory,
    boolean
> => {
    const consentCookie = readCookie(consentCookieName);
    const categories = consentCookie
        ? parseConsentCookie(consentCookie)
        : defaultCookieConsent();

    return categories;
};

export const consentToAllCookiesForTesting = () => {
    createCookie(
        consentCookieName,
        CookieCategory.StrictlyNecessary,
        `isGpcEnabled=0&datestamp=Tue+Sep+26+2023+15%3A54%3A40+GMT-0400+(Eastern+Daylight+Saving+Time)&version=202308.2.0&browserGpcFlag=0&isIABGlobal=false&hosts=&landingPath=NotLandingPage&groups=C0001%3A1%2CC0002%3A1%2CC0003%3A1%2CC0004%3A1`,
    );
};
